import { Typography } from '@/components/app'
import { SectionTitle } from './SectionTitle'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import 'swiper/css'
import { Star } from 'lucide-react'
import { motion } from 'framer-motion'
import { fadeUp, viewportOnce } from '@/lib/animations'

interface Testimonial {
  id: number
  name: string
  role: string
  avatar: string
  rating: number
  review: string
}

interface TestimonialSliderProps {
  testimonials: Testimonial[]
  reverse?: boolean
  className?: string
}

const TestimonialSlider = ({ testimonials, reverse = false, className }: TestimonialSliderProps) => {
  return (
    <div className="relative">
      <div className="absolute left-0 top-0 bottom-0 w-[14vw] bg-linear-to-r from-background/80 to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-[14vw] bg-linear-to-l from-background/80 to-transparent z-10 pointer-events-none" />

      <Swiper
        modules={[Autoplay]}
        autoplay={{ delay: 500, disableOnInteraction: false, reverseDirection: reverse }}
        speed={7000}
        loop={true}
        slidesPerView="auto"
        spaceBetween={24}
        className={className}
      >
        {[...testimonials, ...testimonials].map((testimonial, index) => (
          <SwiperSlide key={`${className}-${index}`} style={{ width: 'auto', height: 'auto' }}>
            <motion.div
              className="w-[80vw] sm:w-[620px] h-full bg-background border border-border-light rounded-3xl p-4 md:p-6"
              whileHover={{ y: -4, boxShadow: '0 12px 32px rgba(0,0,0,0.08)', transition: { duration: 0.25 } }}
            >
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 pb-3 border-b mb-3">
                  <div className="w-[56px] h-[56px] rounded-full overflow-hidden bg-muted">
                    <img src={testimonial.avatar} alt={testimonial.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <Typography variant="body2" className="font-semibold">{testimonial.name}</Typography>
                    <Typography variant="caption" color="muted">{testimonial.role}</Typography>
                  </div>
                </div>
                <div className="flex gap-1">
                  {Array.from({ length: testimonial.rating }).map((_, starIndex) => (
                    <Star key={starIndex} size={16} className="fill-[#FF7F22] text-[#FF7F22]" />
                  ))}
                </div>
              </div>
              <Typography variant="body1">{testimonial.review}</Typography>
            </motion.div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  )
}

const testimonialsRow1: Testimonial[] = [
  { id: 1, name: 'Crystal Maiden', role: 'UI/UX Designer', avatar: '/images/login-img.webp', rating: 5, review: "I've used other kits, but this one is the best. The attention to detail and usability are truly amazing for all designers. I highly recommend it for any type of project." },
  { id: 2, name: 'Dazzie Healer', role: 'Front End Developer', avatar: '/images/login-img.webp', rating: 5, review: "I've used other kits, but this one is the best. The attention to detail and usability are truly amazing for all designers. I highly recommend it for any type of project." },
  { id: 3, name: 'Crystal Maiden', role: 'UI/UX Designer', avatar: '/images/login-img.webp', rating: 5, review: "I've used other kits, but this one is the best. The attention to detail and usability are truly amazing for all designers. I highly recommend it for any type of project." },
]

const testimonialsRow2: Testimonial[] = [
  { id: 4, name: 'Crystal Maiden', role: 'UI/UX Designer', avatar: '/images/login-img.webp', rating: 5, review: "I've used other kits, but this one is the best. The attention to detail and usability are truly amazing for all designers. I highly recommend it for any type of project." },
  { id: 5, name: 'Mirana Marci', role: '3D Designer', avatar: '/images/login-img.webp', rating: 5, review: "I've used other kits, but this one is the best. The attention to detail and usability are truly amazing for all designers. I highly recommend it for any type of project." },
  { id: 6, name: 'Bimosaurus', role: 'Graphic Designer', avatar: '/images/login-img.webp', rating: 5, review: "I've used other kits, but this one is the best. The attention to detail and usability are truly amazing for all designers. I highly recommend it for any type of project." },
  { id: 7, name: 'Crystal Maiden', role: 'UI/UX Designer', avatar: '/images/login-img.webp', rating: 5, review: "I've used other kits, but this one is the best. The attention to detail and usability are truly amazing for all designers. I highly recommend it for any type of project." },
]

export function TestimonialsSection() {
  return (
    <section id="testimonials" className="py-6 md:py-10 lg:py-12 relative overflow-hidden">
      <div className="">
        {/* Section Title */}
        <motion.div
          className="mb-12 md:mb-16"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          <SectionTitle
            badge="TESTIMONIALS"
            title="What Our Customers Say"
            subtitle="Thousands of happy customers trust us for their jewellery purchases."
            centered
          />
        </motion.div>

        {/* Testimonials Sliders */}
        <div className="space-y-4 lg:space-y-6">
          <TestimonialSlider testimonials={testimonialsRow1} reverse={false} className="testimonials-swiper-row1" />
          <TestimonialSlider testimonials={testimonialsRow2} reverse={true} className="testimonials-swiper-row2" />
        </div>
      </div>
    </section>
  )
}
